/**
 * Story 049 — TanStack Query hook for the per-user Dashboard layout.
 *
 * GET on mount with `staleTime: 60_000` per UX brief §"Persistence
 * contract" / story §"Persistence contract". Mutations are coalesced
 * by an internal 600ms debounce — every drag / resize / hide / restore
 * funnels through `saveLayout(envelope)` and the hook timeouts the
 * actual PUT.
 *
 * 5xx on GET surfaces as `isFetchError === true` so the page-level
 * `<LayoutFetchWarning>` banner can render the inline copy from AC12.
 * 4xx (specifically 404 → "no saved layout, use defaults") is NOT an
 * error — it resolves the query with `null` so the page can apply
 * factory defaults silently.
 *
 * No localStorage shadow. The user's headline requirement is
 * cross-device; a localStorage cache would mask cross-device sync
 * failures. See acceptance test AC5 — "headline cross-device
 * assertion."
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { useAuthStore } from "@aeriosiq/auth-client";
import { useCallback, useEffect, useRef } from "react";
import {
  authFetch,
  type AuthFetchError,
} from "../../account/queries/authFetch.js";
import {
  factoryEnvelope,
  type DashboardLayoutEnvelope,
} from "../shared/factoryDefaults.js";
import type { Layout } from "react-grid-layout";

const LAYOUT_PATH = "/me/dashboard-layout";
const DEBOUNCE_MS = 600;

/**
 * R58-W-07 — strip RGL-private fields from the wire envelope.
 *
 * `react-grid-layout` mutates layout entries in-place during drag/resize
 * — it sets `moved: true`, may carry `static: false`, `isDraggable`,
 * `isResizable`, and a `placeholder` marker through. None of those are
 * in the `RglEntry` contract schema (`auth/v1@4.4.0`). The BE accepts
 * the body because its validation pipe uses `whitelist: true`, but
 * keeping the wire payload contract-shaped makes BE log lines and BE
 * integration tests easier to reason about, and avoids the
 * `oasdiff(live, accepted)` drift signal flickering on bodies that
 * carry undeclared keys.
 *
 * Returns a new envelope with each entry pared down to the 7 contract
 * fields. Idempotent — calling it on an already-clean envelope is a
 * no-op other than the allocation.
 */
function sanitiseEnvelope(
  envelope: DashboardLayoutEnvelope,
): DashboardLayoutEnvelope {
  const cleanEntry = (l: Layout): Layout => {
    const out: Layout = {
      i: l.i,
      x: l.x,
      y: l.y,
      w: l.w,
      h: l.h,
    };
    if (typeof l.minW === "number") out.minW = l.minW;
    if (typeof l.minH === "number") out.minH = l.minH;
    return out;
  };
  return {
    version: envelope.version,
    layouts: {
      lg: envelope.layouts.lg.map(cleanEntry),
      md: envelope.layouts.md.map(cleanEntry),
      sm: envelope.layouts.sm.map(cleanEntry),
      xs: envelope.layouts.xs.map(cleanEntry),
    },
    hidden: { ...envelope.hidden },
  };
}

/**
 * R58-W-07 — extract a human-readable detail from an `AuthFetchError`'s
 * parsed body. Used by `DashboardPage`'s error Snackbar to surface the
 * BE's `message` / `code` field instead of a generic "Try again." copy.
 *
 * Story AC3 locks the copy: "Couldn't save dashboard layout — {detail}".
 * The {detail} is whichever of the following the BE body carries:
 *   1. `message`   (free-form, used by NestJS BadRequestException defaults)
 *   2. `code`      (machine-readable; falls back when message is absent)
 *   3. status text (when the body parses to neither — rare; CORS, 5xx
 *      from infra layers like nginx/CloudFront, etc.)
 */
export function extractSaveErrorDetail(err: AuthFetchError): string {
  const body = err.body as
    | { message?: unknown; code?: unknown }
    | null
    | undefined;
  if (body && typeof body === "object") {
    if (typeof body.message === "string" && body.message.length > 0) {
      return body.message;
    }
    if (typeof body.code === "string" && body.code.length > 0) {
      return body.code;
    }
  }
  if (typeof err.status === "number") {
    return `HTTP ${String(err.status)}`;
  }
  return "network error";
}

export const dashboardLayoutKeys = {
  all: ["dashboard", "layout"] as const,
  current: () => [...dashboardLayoutKeys.all, "current"] as const,
};

export interface DashboardLayoutQueryResult {
  /**
   * The persisted envelope, or `null` if the user has never customized
   * (404). Page-level code falls back to factory defaults in that case.
   */
  envelope: DashboardLayoutEnvelope | null;
  /**
   * `true` when the GET resolved with a 5xx. The page should still
   * render with factory defaults but show the inline warning banner
   * (AC12 — "Couldn't load your saved dashboard layout. Showing
   * defaults.").
   */
  isFetchError: boolean;
  isPending: boolean;
  refetch: () => void;
}

interface RawQueryResult {
  envelope: DashboardLayoutEnvelope | null;
  isFetchError: boolean;
}

function isValidEnvelope(
  candidate: unknown,
): candidate is DashboardLayoutEnvelope {
  if (!candidate || typeof candidate !== "object") return false;
  const c = candidate as Partial<DashboardLayoutEnvelope>;
  if (c.version !== 1) return false;
  if (!c.layouts || typeof c.layouts !== "object") return false;
  const l = c.layouts;
  if (
    !Array.isArray(l.lg) ||
    !Array.isArray(l.md) ||
    !Array.isArray(l.sm) ||
    !Array.isArray(l.xs)
  ) {
    return false;
  }
  if (!c.hidden || typeof c.hidden !== "object") return false;
  return true;
}

async function fetchLayout(): Promise<RawQueryResult> {
  try {
    const envelope = await authFetch<DashboardLayoutEnvelope | undefined>(
      LAYOUT_PATH,
    );
    // authFetch resolves 204 / empty body as `undefined`. Treat that
    // as "no saved layout" rather than throwing — defensive against a
    // backend that ships 204 instead of 404 for the empty case.
    if (envelope === undefined) {
      return { envelope: null, isFetchError: false };
    }
    // Reject malformed payloads (a missing breakpoint, version drift,
    // an empty `{}` body returned by an over-eager stub, etc.). The
    // story locks `version: 1` and all four RGL breakpoints; anything
    // else falls back to factory defaults rather than crashing the
    // page on a deep spread of `undefined.lg`.
    if (!isValidEnvelope(envelope)) {
      return { envelope: null, isFetchError: false };
    }
    return { envelope, isFetchError: false };
  } catch (err) {
    const fetchErr = err as AuthFetchError;
    if (typeof fetchErr.status === "number" && fetchErr.status === 404) {
      // 404 is the documented "no saved layout" case — apply defaults.
      return { envelope: null, isFetchError: false };
    }
    if (typeof fetchErr.status === "number" && fetchErr.status >= 500) {
      // 5xx — the page renders factory defaults plus a non-blocking
      // inline warning. Resolve with `isFetchError: true` so we don't
      // hit React Query's retry / error-boundary path; the warning
      // banner is the only UX response to a 5xx GET.
      return { envelope: null, isFetchError: true };
    }
    // 4xx other than 404 — surface as a real error so DevTools picks
    // it up. Caller treats this the same as the 5xx fallback path
    // (defaults + warning) via the `isError` flag from useQuery.
    throw err;
  }
}

/**
 * Read hook. Suspends nothing; the page renders factory defaults
 * immediately and reflows when the GET resolves to a non-default
 * envelope (UX brief §7.1 — "optimistic-render rationale").
 */
export function useDashboardLayoutQuery(): DashboardLayoutQueryResult {
  // Gate the GET on session + tenant being resolved (matches useExpandedMe /
  // useSessions). `authFetch` only attaches the required `X-Tenant-ID` header
  // when `tenant.tenantSlug` is in the store; firing this query on mount —
  // before the tenant resolves — sends no header, so the BE returns
  // 400 "X-Tenant-ID header is required". With `retry: false` that 400 sticks
  // for the whole session (staleTime 60s, no refetch-on-focus), so the saved
  // layout never loads and the user always sees factory defaults. `enabled`
  // defers the GET until the header can be sent; the store update re-enables it.
  const session = useAuthStore((s) => s.session);
  const tenant = useAuthStore((s) => s.tenant);
  const tenantReady =
    !!session && !!tenant && session.tenant.tenantSlug === tenant.tenantSlug;
  const query = useQuery({
    queryKey: dashboardLayoutKeys.current(),
    queryFn: fetchLayout,
    enabled: tenantReady,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  const envelope = query.data?.envelope ?? null;
  const fromQueryError = query.isError || (query.data?.isFetchError ?? false);
  return {
    envelope,
    isFetchError: fromQueryError,
    isPending: query.isPending,
    refetch: () => {
      void query.refetch();
    },
  };
}

interface SaveLayoutVariables {
  envelope: DashboardLayoutEnvelope;
}

async function putLayout({ envelope }: SaveLayoutVariables): Promise<void> {
  // R58-W-07 — sanitise BEFORE serialising; `react-grid-layout`'s in-place
  // mutation otherwise leaks `moved` / `static` / `isDraggable` through.
  await authFetch<undefined>(LAYOUT_PATH, {
    method: "PUT",
    body: sanitiseEnvelope(envelope),
  });
}

/**
 * Imperative save hook. The returned `saveLayout` function debounces
 * the underlying PUT by `DEBOUNCE_MS`. Every layout-changing gesture
 * (drag / resize / hide / restore / reset) calls it; consecutive calls
 * within the debounce window collapse to a single PUT carrying the
 * latest envelope.
 *
 * Reset is intentionally NOT bypassed — the AC4 budget allows up to
 * 1.5s for the PUT to fire after the gesture, so a 600ms debounce + a
 * single network round-trip fits comfortably.
 */
export function useDashboardLayoutMutation(): {
  saveLayout: (envelope: DashboardLayoutEnvelope) => void;
  isSaving: boolean;
  isError: boolean;
  /**
   * R58-W-07 — last error detail, derived from the BE's response body.
   * `null` when the mutation has never errored or its error has been
   * cleared by a subsequent successful PUT. The Snackbar in
   * `DashboardPage` reads this to build the AC3 copy
   * "Couldn't save dashboard layout — {detail}".
   */
  errorDetail: string | null;
} {
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: putLayout,
    onSuccess: (_data, variables) => {
      // Sync the cached envelope so subsequent refetches see the
      // server-of-record state without a network round-trip.
      qc.setQueryData(dashboardLayoutKeys.current(), {
        envelope: sanitiseEnvelope(variables.envelope),
        isFetchError: false,
      });
    },
  });

  const pendingRef = useRef<DashboardLayoutEnvelope | null>(null);
  const timerRef = useRef<number | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const next = pendingRef.current;
    if (!next) return;
    pendingRef.current = null;
    mutation.mutate({ envelope: next });
  }, [mutation]);

  const saveLayout = useCallback(
    (envelope: DashboardLayoutEnvelope) => {
      pendingRef.current = envelope;
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        const next = pendingRef.current;
        if (!next) return;
        pendingRef.current = null;
        mutation.mutate({ envelope: next });
      }, DEBOUNCE_MS);
    },
    [mutation],
  );

  // Flush any pending debounce on unmount so a quick gesture-then-
  // navigation doesn't drop the user's last edit on the floor.
  useEffect(() => {
    return () => {
      flush();
    };
  }, [flush]);

  // TanStack Query v5 invariant: when `isError === true`, `error` is non-
  // null. The redundancy of checking both made ESLint's
  // `no-unnecessary-condition` rule flag this — narrow to the single
  // load-bearing check.
  const errorDetail = mutation.isError
    ? extractSaveErrorDetail(mutation.error as AuthFetchError)
    : null;

  return {
    saveLayout,
    isSaving: mutation.isPending,
    isError: mutation.isError,
    errorDetail,
  };
}

/**
 * Test-only seed helper. Lets unit tests prime the cache without
 * monkey-patching the network layer. Dev code should never call this.
 */
export function __seedDashboardLayout(
  client: QueryClient,
  envelope: DashboardLayoutEnvelope = factoryEnvelope(),
): void {
  client.setQueryData(dashboardLayoutKeys.current(), {
    envelope,
    isFetchError: false,
  });
}
